import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, Select, ConfirmDeleteButton } from "./_helpers";
import MapPicker from "@/components/admin/MapPicker";
import { toast } from "sonner";
import {
  Inbox, CheckCircle2, XCircle, Phone, User as UserI, MapPin, Globe, Tag,
} from "lucide-react";

const emptyForm = {
  name: "",
  category: "",
  province_id: "",
  district: "",
  address: "",
  phone: "",
  website: "",
  lat: "",
  lng: "",
  description: "",
  is_active: true,
  status: "approved",
};

export default function AdminCityGuide() {
  const [approved, setApproved] = useState([]);
  const [pending, setPending] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [f, setF] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState("pending");

  const load = async () => {
    try {
      // Fetch ALL pages so admins never hit the 200-item cap.
      const fetchAll = async (status) => {
        const PAGE = 200;
        let skip = 0;
        const all = [];
        while (true) {
          const r = await api.get(
            `/city-guide?status=${status}&admin=true&limit=${PAGE}&skip=${skip}`,
          );
          const items = r.data.items || [];
          all.push(...items);
          const total = r.data.total ?? all.length;
          if (all.length >= total || items.length < PAGE) break;
          skip += PAGE;
          if (skip > 10000) break; // hard safety stop
        }
        return all;
      };
      const [appr, pend] = await Promise.all([
        fetchAll("approved"),
        fetchAll("pending"),
      ]);
      setApproved(appr);
      setPending(pend);
    } catch {
      toast.error("Yüklenemedi");
    }
  };

  useEffect(() => {
    load();
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
  }, []);

  const provLookup = useMemo(() => {
    const m = {};
    provinces.forEach((p) => (m[p.id] = p.name));
    return m;
  }, [provinces]);

  const resetForm = () => {
    setEditingId(null);
    setF(emptyForm);
  };

  const save = async () => {
    if (!f.name?.trim()) return toast.error("Ad zorunlu");
    if (!f.province_id) return toast.error("İl seçimi zorunlu");
    const payload = {
      ...f,
      province_id: f.province_id || null,
      lat: f.lat !== "" && f.lat !== null ? +f.lat : null,
      lng: f.lng !== "" && f.lng !== null ? +f.lng : null,
    };
    try {
      if (editingId) await api.put(`/city-guide/${editingId}`, payload);
      else await api.post("/city-guide", payload);
      toast.success(editingId ? "Güncellendi" : "Eklendi");
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
    }
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setF({
      ...emptyForm,
      ...e,
      lat: e.lat ?? "",
      lng: e.lng ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const approve = async (id) => {
    try {
      await api.post(`/city-guide/${id}/approve`);
      toast.success("Öneri onaylandı ve yayına alındı");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onaylanamadı");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Bu öneri reddedilecek. Emin misiniz?")) return;
    try {
      await api.post(`/city-guide/${id}/reject`);
      toast.success("Öneri reddedildi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reddedilemedi");
    }
  };

  const list = tab === "pending" ? pending : approved;

  return (
    <div data-testid="admin-cityguide-page">
      <AdminPageHeader
        title="Şehir Rehberi Yönetimi"
        subtitle="Vatandaşlardan gelen işletme/mekân önerilerini onaylayın veya reddedin."
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        <TabButton
          active={tab === "pending"}
          onClick={() => setTab("pending")}
          testId="admin-cityguide-tab-pending"
        >
          <Inbox className="w-4 h-4" /> Onay Bekleyenler
          {pending.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#e11d2a] text-white">
              {pending.length}
            </span>
          )}
        </TabButton>
        <TabButton
          active={tab === "approved"}
          onClick={() => setTab("approved")}
          testId="admin-cityguide-tab-approved"
        >
          <CheckCircle2 className="w-4 h-4" /> Onaylı Kayıtlar ({approved.length})
        </TabButton>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div
          className="bg-white border border-gray-100 rounded-lg p-4 max-h-[700px] overflow-y-auto"
          data-testid={`admin-cityguide-${tab}-list`}
        >
          {list.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-10">
              {tab === "pending" ? "Onay bekleyen öneri yok" : "Henüz onaylı kayıt yok"}
            </p>
          )}
          {list.map((e) => (
            <div
              key={e.id}
              className={`py-3 px-2 -mx-2 border-b last:border-0 ${
                tab === "pending" ? "bg-amber-50/40 rounded-md mb-1.5 border-b-0" : ""
              }`}
            >
              <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                <span className="truncate max-w-[280px]">{e.name}</span>
                {e.category && (
                  <span className="text-[10px] uppercase bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                    <Tag className="w-2.5 h-2.5" /> {e.category}
                  </span>
                )}
                {e.province_id && provLookup[e.province_id] && (
                  <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                    {provLookup[e.province_id]}
                    {e.district ? ` / ${e.district}` : ""}
                  </span>
                )}
              </div>

              {e.address && (
                <div className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                  <span className="line-clamp-1">{e.address}</span>
                  {e.lat != null && e.lng != null && (
                    <a
                      href={`https://www.google.com/maps?q=${e.lat},${e.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline ml-1 shrink-0"
                    >
                      haritada
                    </a>
                  )}
                </div>
              )}

              {(e.phone || e.website) && (
                <div className="text-xs text-gray-600 mt-1 flex items-center gap-3 flex-wrap">
                  {e.phone && (
                    <a
                      href={`tel:${e.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-1 hover:text-[#e11d2a]"
                    >
                      <Phone className="w-3 h-3" /> {e.phone}
                    </a>
                  )}
                  {e.website && (
                    <a
                      href={e.website}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 hover:text-[#e11d2a]"
                    >
                      <Globe className="w-3 h-3" /> {e.website}
                    </a>
                  )}
                </div>
              )}

              {tab === "pending" && e.description && (
                <p className="text-xs text-gray-600 mt-2 line-clamp-3 italic border-l-2 border-amber-300 pl-2">
                  “{e.description}”
                </p>
              )}

              <div className="flex flex-wrap gap-2 justify-end mt-2 pt-1.5 border-t border-gray-100">
                {tab === "pending" && (
                  <>
                    <button
                      onClick={() => approve(e.id)}
                      data-testid={`admin-cityguide-approve-${e.id}`}
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded font-semibold"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Onayla
                    </button>
                    <button
                      onClick={() => reject(e.id)}
                      data-testid={`admin-cityguide-reject-${e.id}`}
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded font-semibold"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reddet
                    </button>
                  </>
                )}
                <button
                  onClick={() => openEdit(e)}
                  className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50"
                >
                  Düzenle
                </button>
                <ConfirmDeleteButton
                  onConfirm={async () => {
                    await api.delete(`/city-guide/${e.id}`);
                    load();
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-2 max-h-[700px] overflow-y-auto">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">{editingId ? "Kaydı Düzenle" : "Yeni Kayıt"}</h3>
            {editingId && (
              <button onClick={resetForm} className="text-xs text-gray-500 hover:underline">
                İptal
              </button>
            )}
          </div>
          <Field label="Ad *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Field
            label="Kategori"
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })}
            placeholder="örn. Restoran, Otel, Müze"
          />
          <Select
            label="İl *"
            value={f.province_id}
            options={[{ value: "", label: "-" }, ...provinces.map((p) => ({ value: p.id, label: p.name }))]}
            onChange={(e) => setF({ ...f, province_id: e.target.value })}
          />
          <Field label="İlçe" value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} />
          <Field label="Adres" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          <Field label="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <Field label="Web Sitesi" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">
              Konum (haritadan seçin)
            </label>
            <MapPicker
              lat={f.lat}
              lng={f.lng}
              onChange={({ lat, lng }) => setF({ ...f, lat: lat ?? "", lng: lng ?? "" })}
              defaultQuery={[
                f.name,
                f.address,
                f.district,
                provinces.find((p) => p.id === f.province_id)?.name,
              ]
                .filter(Boolean)
                .join(" ")}
              height={240}
            />
          </div>
          <label className="block text-xs font-medium text-gray-600">Açıklama</label>
          <textarea
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
            placeholder="Kısa açıklama"
          />
          <button
            onClick={save}
            className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold hover:bg-[#c01825]"
            data-testid="admin-cityguide-save"
          >
            {editingId ? "Güncelle" : "Ekle"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-[2px] transition-colors ${
        active
          ? "border-[#e11d2a] text-[#e11d2a]"
          : "border-transparent text-gray-600 hover:text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}
